import React, { useMemo, useState } from "react";

/*
 * The preview and the result of a mass update or mass complete (the API's plan: one row per
 * trial with an outcome, the changes, what else happens, and why a trial is skipped). Shared by
 * UpdateMultipleTrialsComponent (fields) and MassCompleteTasks (tasks).
 */

const TONES = {
  green: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200",
  blue: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200",
  gray: "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200",
  amber: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200",
};

/**
 * The preview: a table of the selected trials with what happens to each. Trials whose outcome
 * is in `applicable` can be left out with their checkbox; only the ones kept are applied.
 *
 * outcomes: { [outcome]: { label, tone } }, e.g. { change: { label: "Will change", tone: "green" } }
 * describe(row): the details shown for a trial (changes, effects or the reason)
 * onConfirm(ids): called with the ids of the applicable trials that were kept
 */
export function MassPreviewDialog({ title, rows, outcomes, applicable, describe, confirmText, busy, onConfirm, onCancel }) {
  // A ref-free Set in state: leaving a trial out re-renders the count on the confirm button
  const [excluded, setExcluded] = useState(() => new Set());
  const kept = useMemo(
    () => rows.filter((row) => applicable.includes(row.outcome) && !excluded.has(row.id)).map((row) => row.id),
    [rows, applicable, excluded]
  );
  const toggle = (id) =>
    setExcluded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
      <div
        role="dialog"
        aria-label="Mass update preview"
        className="flex max-h-[85vh] w-[56rem] max-w-[95vw] flex-col rounded-md border border-gray-200 bg-white p-6 shadow-lg dark:border-gray-700 dark:bg-gray-800"
      >
        <div className="mb-3 text-base font-semibold text-gray-900 dark:text-gray-100">{title}</div>
        <div className="flex-1 overflow-auto rounded border border-gray-200 dark:border-gray-700">
          <table className="min-w-full text-sm">
            <thead className="sticky top-0 bg-gray-100 dark:bg-gray-700">
              <tr>
                <th className="px-3 py-2 text-left font-semibold">Apply</th>
                <th className="px-3 py-2 text-left font-semibold">Trial</th>
                <th className="px-3 py-2 text-left font-semibold">Status</th>
                <th className="px-3 py-2 text-left font-semibold">Details</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const canApply = applicable.includes(row.outcome);
                const outcome = outcomes[row.outcome] ?? { label: row.outcome, tone: "gray" };
                return (
                  <tr key={row.id} data-testid="preview-row" className="border-t border-gray-100 align-top dark:border-gray-700">
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        aria-label={`Apply to trial ${row.trialno ?? row.id}`}
                        checked={canApply && !excluded.has(row.id)}
                        disabled={!canApply || busy}
                        onChange={() => toggle(row.id)}
                      />
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 font-medium">{row.trialno ?? row.id}</td>
                    <td className="whitespace-nowrap px-3 py-2">
                      <span className={`rounded px-2 py-0.5 text-xs font-semibold ${TONES[outcome.tone]}`}>{outcome.label}</span>
                    </td>
                    <td className="px-3 py-2 text-gray-800 dark:text-gray-200">{describe(row)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="rounded-md border border-gray-200 bg-white px-3 py-2 text-gray-900 dark:border-gray-700 dark:bg-gray-700 dark:text-gray-100"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onConfirm(kept)}
            disabled={busy || kept.length === 0}
            className="rounded-md bg-emerald-600 px-3 py-2 font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy ? "Saving…" : `${confirmText} ${kept.length} trial${kept.length === 1 ? "" : "s"}`}
          </button>
        </div>
      </div>
    </div>
  );
}

/** The result after saving: one summary line, the skipped trials with the reason, and an optional note. */
export function MassResult({ summary, skipped = [], note, onClose }) {
  return (
    <div role="status" className="mt-4 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-gray-900 dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-gray-100">
      <div className="flex items-start justify-between gap-3">
        <div className="font-semibold">{summary}</div>
        {onClose && (
          <button type="button" onClick={onClose} className="text-xs underline">
            Close
          </button>
        )}
      </div>
      {skipped.length > 0 && (
        <div className="mt-2">
          <div className="font-medium">Skipped ({skipped.length}):</div>
          <ul className="list-disc pl-5">
            {skipped.map((row) => (
              <li key={row.id}>
                <span className="font-medium">{row.trialno ?? row.id}</span>: {row.reason}
              </li>
            ))}
          </ul>
        </div>
      )}
      {note && <div className="mt-2 italic">{note}</div>}
    </div>
  );
}

/** An API error as the user sees it, with Retry. */
export function MassError({ message, onRetry, disabled }) {
  return (
    <div className="mt-2 flex items-center gap-3 text-red-600 dark:text-red-400">
      <span>{message}</span>
      <button
        type="button"
        onClick={onRetry}
        disabled={disabled}
        className="rounded-lg border border-red-300 px-2 py-1 text-sm font-semibold hover:bg-red-50 dark:border-red-700 dark:hover:bg-red-900/30"
      >
        Retry
      </button>
    </div>
  );
}
